// CRM Company record — colourful, contacts as the spine. Ports CompanyPanel.
import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import { useAuth, useUser } from '@clerk/clerk-react';
import toast from 'react-hot-toast';
import API_BASE from '../../config';
import { creditAwareFetch } from '../../hooks/creditAwareFetch';
import CrmSequenceSettings from './CrmSequenceSettings';
import CrmCompanyIntelligence from './CrmCompanyIntelligence';
import CrmEmailTimeline from './CrmEmailTimeline';
import CrmEmailComposer from './CrmEmailComposer';
import { DealDrawer } from './DealDrawer';
import SectorPicker from '../../components/SectorPicker';
import { parseSectors } from '../../components/sectors';

const AV_COLOURS = ['#7C5CFC','#00A878','#2E8FFF','#F5A623','#F43F5E','#06B6D4','#EC4899','#64748B','#FF7A59','#8B5CF6','#0EA5E9','#F97316','#10B981','#6366F1','#D946EF','#14B8A6','#EAB308','#EF4444'];
function avatarColour(name) { const s = String(name || ''); let h = 0; for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0; return AV_COLOURS[h % AV_COLOURS.length]; }
function initials(name) {
  const p = String(name || '').trim().split(/\s+/);
  return ((p[0]?.[0] || '') + (p[1]?.[0] || '')).toUpperCase() || '?';
}
function Cofav({ name, website }) {
  const [failed, setFailed] = useState(false);
  const dom = website ? String(website).replace(/^https?:\/\/(www\.)?/, '').split('/')[0] : '';
  if (!dom || failed) return <span className="crmr-cofav" style={{ background: 'linear-gradient(135deg,#FF7A59,#EC4899)' }}>{initials(name)}</span>;
  return <span className="crmr-cofav"><img src={`https://www.google.com/s2/favicons?domain=${dom}&sz=64`} alt="" onError={() => setFailed(true)} /></span>;
}

const SIGNAL = {
  funding: { c: '#056F4E', bg: '#D1F0E4', l: 'Funding', icon: '$' },
  leadership_change: { c: '#5B3FD6', bg: '#E7E1FD', l: 'Leadership', icon: '★' },
  hiring: { c: '#0B5BB5', bg: '#D9E9FB', l: 'Hiring', icon: '↗' },
  tech_change: { c: '#066576', bg: '#CFF0F6', l: 'Tech', icon: '⚙' },
  news: { c: '#3F4956', bg: '#E3E7ED', l: 'News', icon: '◆' },
};
function relTime(v) { if (!v) return ''; const d = new Date(v); if (isNaN(d)) return ''; const diff = Date.now() - d.getTime(); const day = 86400000; if (diff < day) return 'Today'; if (diff < 2 * day) return 'Yesterday'; if (diff < 30 * day) return Math.floor(diff / day) + ' days ago'; return ukDate(v); }
const SIGNAL_LABELS = { funding: 'Funding round', hiring: 'Hiring activity', leadership_change: 'Leadership change', news: 'Company news', tech_change: 'Technology change' };
const TABS = ['Intel', 'Signals', 'Contacts', 'Activity', 'Notes', 'Seq settings'];
const EDIT_FIELDS = [
  { key: 'name', label: 'Company name' },
  { key: 'sector', label: 'Sector' },
  { key: 'size', label: 'Size' },
  { key: 'location', label: 'Location' },
  { key: 'website', label: 'Website' },
  { key: 'linkedin_url', label: 'LinkedIn URL' },
];

function ukDate(v) { if (!v) return ''; const d = new Date(v); return isNaN(d) ? '' : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); }
function Prop({ k, v }) { return <div className="crmr-prop"><span className="crmr-prop-k">{k}</span><span className="crmr-prop-v">{v}</span></div>; }

export default function CrmCompanyRecord() {
  const params = useParams();
  const navigate = useNavigate();
  const { clientId } = useOutletContext();
  const { getToken } = useAuth();
  const { user } = useUser();
  const userName = user?.fullName || user?.primaryEmailAddress?.emailAddress?.split('@')[0] || 'You';
  const companyId = params.companyId;
  const baseClientId = clientId;

  const [company, setCompany] = useState(null);
  const [signals, setSignals] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [compose, setCompose] = useState(null); // null | { contact, replyTo }
  const [emailRefresh, setEmailRefresh] = useState(0);
  const [emailTo, setEmailTo] = useState('');
  const [notes, setNotes] = useState([]);
  const [sweepMeta, setSweepMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [tab, setTab] = useState('Intel');
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  const [results, setResults] = useState(null);
  const [selected, setSelected] = useState(new Set());

  // add-contact modal (mirrors the contact record flow)
  const [showAddContact, setShowAddContact] = useState(false);
  const [addForm, setAddForm] = useState({ linkedin_url: '', full_name: '', job_title: '', email: '', photo_url: '' });
  const [fetchingLi, setFetchingLi] = useState(false);
  const [creatingContact, setCreatingContact] = useState(false);
  const [deals, setDeals] = useState([]);
  const [pipelines, setPipelines] = useState([]);
  const [dealDrawer, setDealDrawer] = useState(null);

  function openAddContact() { setAddForm({ linkedin_url: '', full_name: '', job_title: '', email: '', photo_url: '' }); setShowAddContact(true); }
  async function fetchFromLinkedin() {
    const url = addForm.linkedin_url.trim();
    if (!url.toLowerCase().includes('linkedin.com/in/')) { toast.error('Enter a valid LinkedIn profile URL'); return; }
    setFetchingLi(true);
    const tId = toast.loading('Reading LinkedIn profile…');
    try {
      const r = await fetch(`${API_BASE}/parse-linkedin`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ linkedin_url: url }) });
      const d = await r.json();
      if (!r.ok) { toast.error(d.error || 'Could not read that profile', { id: tId }); }
      else if (d.found) { setAddForm(p => ({ ...p, full_name: d.full_name || p.full_name, job_title: d.job_title || p.job_title, photo_url: d.photo_url || p.photo_url })); toast.success('Prefilled from LinkedIn', { id: tId }); }
      else { toast('No details found — fill the form manually', { id: tId }); }
    } catch (e) { toast.error('Lookup failed', { id: tId }); }
    setFetchingLi(false);
  }
  async function createContact() {
    if (!addForm.full_name.trim()) { toast.error('Name is required'); return; }
    setCreatingContact(true);
    try {
      const r = await fetch(`${API_BASE}/companies/${companyId}/contacts`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ client_id: baseClientId, full_name: addForm.full_name.trim(), job_title: addForm.job_title.trim(), email: addForm.email.trim(), linkedin_url: addForm.linkedin_url.trim(), photo_url: addForm.photo_url || '' }) });
      const d = await r.json();
      if (r.status === 409) { toast.error(d.message || 'That contact already exists'); setCreatingContact(false); if (d.existing_id) { setShowAddContact(false); navigate('/clients/' + baseClientId + '/crm/contacts/' + d.existing_id); } return; }
      if (!r.ok) { toast.error(d.error || 'Could not create contact'); setCreatingContact(false); return; }
      toast.success('Contact added');
      setShowAddContact(false);
      const ctR = await fetch(`${API_BASE}/companies/${companyId}/contacts`).then(x => x.json());
      setContacts(Array.isArray(ctR) ? ctR : []);
    } catch (e) { toast.error('Could not create contact'); }
    setCreatingContact(false);
  }

  const backToList = () => navigate(`/clients/${baseClientId}/crm/companies`);

  const loadAll = useCallback(async () => {
    if (!companyId) return;
    setLoading(true); setError(null);
    try {
      const [coR, sigR, ctR, ntR, dlR, pipR] = await Promise.all([
        fetch(`${API_BASE}/clients/${baseClientId}/companies`).then(r => r.ok ? r.json() : []),
        fetch(`${API_BASE}/companies/${companyId}/signals`).then(r => r.ok ? r.json() : { signals: [] }),
        fetch(`${API_BASE}/companies/${companyId}/contacts`).then(r => r.ok ? r.json() : []),
        fetch(`${API_BASE}/companies/${companyId}/notes`).then(r => r.ok ? r.json() : []).catch(() => []),
        fetch(`${API_BASE}/clients/${baseClientId}/opportunities?company_id=${companyId}`).then(r => r.ok ? r.json() : []).catch(() => []),
        fetch(`${API_BASE}/clients/${baseClientId}/pipelines`).then(r => r.ok ? r.json() : []).catch(() => []),
      ]);
      const co = Array.isArray(coR) ? coR.find(x => String(x.id) === String(companyId)) : null;
      if (!co) { setError('Company not found.'); setLoading(false); return; }
      setCompany(co);
      setSignals(sigR.signals || []);
      setContacts(Array.isArray(ctR) ? ctR : []);
      setDeals(Array.isArray(dlR) ? dlR : []);
      setPipelines(Array.isArray(pipR) ? pipR : []);
      setNotes(Array.isArray(ntR) ? ntR : []);
      try {
        const token = await getToken();
        const sw = await fetch(`${API_BASE}/clients/${baseClientId}/sweep-status`, { headers: { Authorization: `Bearer ${token}` } });
        if (sw.ok) {
          const data = await sw.json();
          const list = Array.isArray(data) ? data : (data.companies || []);
          const m = list.find(x => String(x.id) === String(companyId));
          if (m) setSweepMeta(m);
        }
      } catch (_) {}
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [companyId, baseClientId, getToken]);
  useEffect(() => { loadAll(); }, [loadAll]);

  function startEdit() {
    setEditForm(EDIT_FIELDS.reduce((a, f) => ({ ...a, [f.key]: company[f.key] || '' }), {}));
    setEditing(true);
  }
  async function saveEdit() {
    setSavingEdit(true);
    try {
      await fetch(`${API_BASE}/companies/${companyId}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(editForm) });
      setCompany(c => ({ ...c, ...editForm }));
      setEditing(false);
      toast.success('Company updated');
    } catch (e) { toast.error('Save failed'); }
    setSavingEdit(false);
  }

  async function scan() {
    setScanning(true);
    const tId = toast.loading('Scanning ' + company.name + ' for signals…');
    try {
      const r = await fetch(`${API_BASE}/companies/${companyId}/detect-signals`, { method: 'POST', headers: { 'Content-Type': 'application/json' } });
      const data = await r.json();
      const sigR = await fetch(`${API_BASE}/companies/${companyId}/signals`).then(x => x.json());
      setSignals(sigR.signals || []);
      const n = (data.signals && data.signals.length) || 0;
      toast.success(n ? `Found ${n} signal${n === 1 ? '' : 's'}` : 'No new signals', { id: tId });
    } catch (e) { toast.error('Scan failed', { id: tId }); }
    setScanning(false);
  }

  async function postNote() {
    if (!newNote.trim()) return;
    setSavingNote(true);
    try {
      const res = await fetch(`${API_BASE}/companies/${companyId}/notes`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ client_id: baseClientId, author_name: userName, content: newNote.trim() }) });
      const note = await res.json();
      setNotes(prev => [...prev, note]);
      setNewNote('');
    } catch (e) {}
    setSavingNote(false);
  }



  if (loading) return <div className="crmr-wrap"><div className="crmr-empty">Loading company…</div></div>;
  if (error || !company) return <div className="crmr-wrap"><button className="crmr-back" onClick={backToList}>← Companies</button><div className="crmr-empty">{error || 'Company not found.'}</div></div>;

  const co = company;
  const webUrl = co.website ? (co.website.startsWith('http') ? co.website : 'https://' + co.website) : null;
  const liUrl = co.linkedin_url ? (co.linkedin_url.startsWith('http') ? co.linkedin_url : 'https://' + co.linkedin_url) : null;
  const sweepStatus = sweepMeta.sweep_status || (!sweepMeta.last_swept_at ? 'never' : (sweepMeta.next_sweep_due && new Date(sweepMeta.next_sweep_due) > new Date() ? 'fresh' : 'due'));
  const SW = { fresh: { cls: 'crmr-sw-green', l: 'Up to date' }, due: { cls: 'crmr-sw-amber', l: 'Needs scan' }, never: { cls: 'crmr-sw-slate', l: 'Not scanned' } };
  const sw = SW[sweepStatus] || SW.never;

  return (
    <div className="crmr-wrap">
      <div className="crmr-topbar"><button className="crmr-back" onClick={backToList}>← Companies</button><span className="crmr-crumb">{co.name}</span></div>

      <div className="crmr-actionbar">
        <button className="crmr-act crmr-act-primary" onClick={scan} disabled={scanning}>{scanning ? 'Scanning…' : 'Scan for signals'}</button>
        <button className="crmr-act crmr-act-ghost" onClick={() => navigate('/clients/' + baseClientId + '/crm/find-similar/companies/' + co.id, { state: { seedLabel: co.name } })}>Find similar →</button>
        {webUrl && <a className="crmr-act crmr-act-blue" href={webUrl} target="_blank" rel="noreferrer">Website →</a>}
        {liUrl && <a className="crmr-act crmr-act-blue" href={liUrl} target="_blank" rel="noreferrer">LinkedIn →</a>}
      </div>

      <div className="crmr-grid">
        <div className="crmr-col crmr-col-left">
          <div className="crmr-idcard">
            <div className="crmr-idtop">
              <Cofav name={co.name} website={co.website} />
              <span className={'crmr-swbadge ' + sw.cls}>{sw.l}</span>
            </div>
            <h1 className="crmr-name">{co.name}</h1>
            {parseSectors(co.sector).length > 0 && (
              <div className="crmr-secpills">
                {parseSectors(co.sector).map((s, i) => { const SEC = ['#5B3FD6','#0B5BB5','#056F4E','#B25400','#066576','#A21C5A','#3F4956','#7A4D00']; let h = 0; for (let k = 0; k < s.length; k++) h = (h * 31 + s.charCodeAt(k)) >>> 0; const c = SEC[h % SEC.length]; return <span key={i} className="crmr-secpill" style={{ color: c, background: c + '1A' }}>{s}</span>; })}
              </div>
            )}
            <p className="crmr-role">{[co.size, co.location].filter(Boolean).join(' · ') || 'No firmographics yet'}</p>
          </div>

          <div className="crmr-card">
            <div className="crmr-cardh">Company information {!editing && <button className="crmr-cardh-edit" onClick={startEdit}>Edit</button>}</div>
            {editing ? (
              <div className="crmr-editform">
                {EDIT_FIELDS.map(({ key, label }) => (
                  <div key={key} className="crmr-editfield"><label>{label}</label>
                    {key === 'sector'
                      ? <SectorPicker value={editForm.sector} onChange={vals => setEditForm(p => ({ ...p, sector: JSON.stringify(vals) }))} />
                      : <input value={editForm[key] || ''} onChange={e => setEditForm(p => ({ ...p, [key]: e.target.value }))} />}
                  </div>
                ))}
                <div className="crmr-editactions"><button className="crmr-act crmr-act-primary" onClick={saveEdit} disabled={savingEdit}>{savingEdit ? 'Saving…' : 'Save changes'}</button><button className="crmr-act crmr-act-ghost" onClick={() => setEditing(false)}>Cancel</button></div>
              </div>
            ) : (
              <div className="crmr-props">
                <Prop k="Sector" v={parseSectors(co.sector).join(', ') || '—'} />
                <Prop k="Size" v={co.size || '—'} />
                <Prop k="Location" v={co.location || '—'} />
                <Prop k="Website" v={webUrl ? <a href={webUrl} target="_blank" rel="noreferrer" className="crmr-link">{co.website}</a> : '—'} />
                <Prop k="LinkedIn" v={liUrl ? <a href={liUrl} target="_blank" rel="noreferrer" className="crmr-link">View profile</a> : '—'} />
                {co.revenue && <Prop k="Revenue" v={co.revenue} />}
                {co.keywords && <Prop k="Keywords" v={co.keywords} />}
              </div>
            )}
          </div>

          {sweepMeta.last_swept_at && (
            <div className="crmr-card">
              <div className="crmr-cardh">Signal scanning</div>
              <div className="crmr-props">
                <Prop k="Last scanned" v={ukDate(sweepMeta.last_swept_at)} />
                {sweepMeta.next_sweep_due && <Prop k="Next due" v={ukDate(sweepMeta.next_sweep_due)} />}
                <Prop k="Total scans" v={sweepMeta.sweep_count || 0} />
              </div>
            </div>
          )}
        </div>

        <div className="crmr-col crmr-col-mid">
          <div className="crmr-card crmr-activity">
            <div className="crmr-tabs">
              {TABS.map(t => (<button key={t} className={'crmr-tab' + (tab === t ? ' crmr-tab--on' : '')} onClick={() => setTab(t)}>{t}{t === 'Signals' && signals.length ? ` (${signals.length})` : ''}{t === 'Contacts' && contacts.length ? ` (${contacts.length})` : ''}{t === 'Notes' && notes.length ? ` (${notes.length})` : ''}</button>))}
            </div>
            <div className="crmr-tabbody">
              {tab === 'Intel' && (
                <CrmCompanyIntelligence companyId={companyId} clientId={baseClientId} layout="centre" onSweepComplete={loadAll} />
              )}

              {tab === 'Signals' && (
                <div className="crmr-siglist">
                  {signals.length === 0 ? (
                    <div className="crmr-empty-sm">No signals detected yet. <button className="crmr-inlinelink" onClick={scan} disabled={scanning}>{scanning ? 'Scanning…' : 'Scan now'}</button></div>
                  ) : signals.map(s => { const m = SIGNAL[s.signal_type] || SIGNAL.news; return (
                    <div className="crmr-sigcard" key={s.id} style={{ background: m.bg, borderLeft: `3px solid ${m.c}` }}>
                      <div className="crmr-sigcard-top"><span className="crmr-sigcard-l" style={{ color: m.c }}>{m.l}</span>{s.relevance_score != null && <span className="crmr-sigcard-s" style={{ color: m.c }}>Score {s.relevance_score}/10</span>}</div>
                      <p className="crmr-sigcard-sum">{(s.signal_data && s.signal_data.summary) || s.summary || ''}</p>
                      <p className="crmr-sigcard-src">{s.source_url ? <a href={s.source_url} target="_blank" rel="noreferrer" className="crmr-link">{s.source}</a> : s.source}{s.detected_at ? ' · ' + ukDate(s.detected_at) : ''}</p>
                    </div>
                  ); })}
                </div>
              )}

              {tab === 'Contacts' && (
                <div className="crmr-cocontacts">
                  <div className="crmr-cocontacts-head">
                    <span>{contacts.length} contact{contacts.length === 1 ? '' : 's'} at {co.name}</span>
                    <button className="crmr-act crmr-act-primary" onClick={openAddContact}>+ Add contact</button>
                  </div>
                  {contacts.length === 0 ? (
                    <div className="crmr-empty-sm">No contacts yet. Add the first one to start working this company.</div>
                  ) : (
                    <div className="crmr-colist">
                      {contacts.map(ct => (
                        <div key={ct.id} className="crmr-corow" onClick={() => navigate(`/clients/${baseClientId}/crm/contacts/${ct.id}`)}>
                          <span className="crmr-avatar crmr-corow-av" style={{ background: 'linear-gradient(135deg,#7C5CFC,#2E8FFF)' }}>{ct.photo_url ? <img src={ct.photo_url} alt="" className="crmr-avatar-img" onError={(e) => { e.target.style.display = 'none'; }} /> : initials(ct.full_name)}</span>
                          <div className="crmr-corow-body">
                            <div className="crmr-corow-name">{ct.full_name}</div>
                            <div className="crmr-corow-sub">{[ct.job_title, ct.email].filter(Boolean).join(' · ') || '—'}</div>
                          </div>
                          <span className="crmr-corow-go">View →</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {tab === 'Activity' && (
                <div className="crmr-emails">
                  {(() => {
                    const emailable = contacts.filter(x => x.email);
                    const pick = emailable.find(x => String(x.id) === String(emailTo)) || emailable[0] || null;
                    return (
                      <div className="crmr-emails-head">
                        {emailable.length === 0 ? <span>No contact here has an email address yet</span> : (
                          <select className="crmr-emails-sel" value={pick ? pick.id : ''} onChange={e => setEmailTo(e.target.value)}>
                            {emailable.map(x => <option key={x.id} value={x.id}>{x.full_name || x.email}{x.job_title ? ` · ${x.job_title}` : ''}</option>)}
                          </select>
                        )}
                        <button className="crmr-act crmr-act-primary" disabled={!pick} onClick={() => pick && setCompose({ contact: { id: pick.id, full_name: pick.full_name, email: pick.email }, replyTo: null })}>New email</button>
                      </div>
                    );
                  })()}
                  <CrmEmailTimeline
                    url={`${API_BASE}/conversations/by-company/${companyId}`}
                    showContact={true}
                    refreshKey={emailRefresh}
                    onReply={(m) => {
                      const ct = contacts.find(x => x.id === m.contact_id);
                      if (!ct || !ct.email) { toast.error('That contact has no email address'); return; }
                      setCompose({ contact: { id: ct.id, full_name: ct.full_name, email: ct.email }, replyTo: { type: m.direction === 'inbound' ? 'inbound' : 'outbound', id: m.id, subject: m.subject } });
                    }}
                    emptyLabel={`No emails yet. All sent and received messages across ${co.name}'s contacts will appear here.`}
                  />
                </div>
              )}

              {tab === 'Notes' && (
                <div className="crmr-notes">
                  <div className="crmr-notelist">
                    {notes.length === 0 && <div className="crmr-empty-sm">No notes yet. Add the first one below.</div>}
                    {notes.map(note => { const isMe = (note.author_name || '') === userName; return (
                      <div className={'crmr-notebubble-row' + (isMe ? ' crmr-note-me' : '')} key={note.id}>
                        <span className="crmr-noteav" style={{ background: isMe ? '#FF7A59' : avatarColour(note.author_name || 'Team') }}>{initials(note.author_name || 'Team')}</span>
                        <div className="crmr-notebody">
                          <div className="crmr-notemeta"><span>{note.author_name || 'Team'}</span><span className="crmr-notetime">{note.created_at ? new Date(note.created_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}</span></div>
                          <div className={'crmr-notebubble' + (isMe ? ' crmr-notebubble-me' : '')} style={isMe ? undefined : (() => { const ac = avatarColour(note.author_name || 'Team'); return { background: ac + '22', color: '#16181D', borderLeft: `3px solid ${ac}` }; })()}>{note.content}</div>
                        </div>
                      </div>
                    ); })}
                  </div>
                  <div className="crmr-notecompose"><textarea value={newNote} onChange={e => setNewNote(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); postNote(); } }} placeholder="Add a note… (Enter to send, Shift+Enter for new line)" rows={2} /><button className="crmr-act crmr-act-primary" onClick={postNote} disabled={savingNote || !newNote.trim()}>{savingNote ? '…' : 'Send'}</button></div>
                </div>
              )}

              {tab === 'Seq settings' && (
                <CrmSequenceSettings level="company" id={companyId} />
              )}
            </div>
          </div>
        </div>

        <div className="crmr-col crmr-col-right">
          <div className="crmr-card">
            <div className="crmr-cardh">Contacts ({contacts.length})<button className="crmr-cardh-add" onClick={openAddContact} title="Add a contact at this company">+ Add contact</button></div>
            {contacts.length === 0 ? (
              <div className="crmr-empty-sm">No contacts yet.</div>
            ) : (
              <div className="crmr-corblock">
                {contacts.slice(0, 5).map(ct => (
                  <div key={ct.id} className="crmr-cormini" onClick={() => navigate(`/clients/${baseClientId}/crm/contacts/${ct.id}`)}>
                    <span className="crmr-avatar crmr-cormini-av" style={{ background: 'linear-gradient(135deg,#7C5CFC,#2E8FFF)' }}>{ct.photo_url ? <img src={ct.photo_url} alt="" className="crmr-avatar-img" onError={(e) => { e.target.style.display = 'none'; }} /> : initials(ct.full_name)}</span>
                    <div className="crmr-cormini-body"><div className="crmr-cormini-name">{ct.full_name}</div><div className="crmr-cormini-sub">{ct.job_title || '—'}</div></div>
                  </div>
                ))}
                {contacts.length > 5 && <button className="crmr-inlinelink" onClick={() => setTab('Contacts')}>View all {contacts.length} →</button>}
              </div>
            )}
          </div>

          <div className="crmr-card">
            <div className="crmr-cardh">Buying signals</div>
            {signals.length === 0 ? (
              <div className="crmr-empty-sm">No signals in the last 90 days.</div>
            ) : (
              <div className="crmr-signals">
                {signals.slice(0, 5).map(s => { const m = SIGNAL[s.signal_type] || SIGNAL.news; return (
                  <div className="crmr-sigrow" key={s.id}><span className="crmr-sigico" style={{ background: m.bg, color: m.c }}>{m.icon}</span><div className="crmr-sigbody"><span className="crmr-sigchip" style={{ background: m.bg, color: m.c }}>{m.l}</span><div className="crmr-sigtext">{(s.signal_data && s.signal_data.summary) || m.l}</div>{s.detected_at && <div className="crmr-sigdate">{relTime(s.detected_at)}</div>}</div></div>
                ); })}
              </div>
            )}
          </div>

          <div className="crmr-card">
            <div className="crmr-cardh">Deals <span className="crmr-cardh-x">{deals.length}</span><button className="crmr-cardh-add" onClick={() => setDealDrawer({})} title="Add a deal">+ Add</button></div>
            {deals.length === 0 ? (
              <div className="crmr-dealempty"><div className="crmr-dealempty-ico">◇</div><p>No deals yet. Track revenue opportunities for this company here.</p></div>
            ) : (
              <div className="crmr-deallist">
                {deals.map(d => {
                  const sym = { GBP: '\u00A3', USD: '$', EUR: '\u20AC', NGN: '\u20A6', CAD: 'CA$', AUD: 'A$' }[d.currency] || '\u00A3';
                  return (
                    <button key={d.id} className="crmr-dealrow" onClick={() => navigate(`/clients/${baseClientId}/crm/opportunities/${d.id}`)}>
                      <span className="crmr-dealdot" style={{ background: d.stage_colour || '#64748B' }} />
                      <span className="crmr-dealnm">{d.name}</span>
                      <span className="crmr-dealstage">{d.stage_name}</span>
                      <span className="crmr-dealval">{sym}{Number(d.value || 0).toLocaleString()}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="crmr-card">
            <div className="crmr-cardh">Attachments<button className="crmr-cardh-add" onClick={() => toast('Attachments are coming soon')} title="Add an attachment">+ Add</button></div>
            <div className="crmr-dealempty"><div className="crmr-dealempty-ico">▢</div><p>No attachments yet. Files linked to this company will appear here.</p></div>
          </div>

          <div className="crmr-card">
            <div className="crmr-cardh">Tickets <span className="crmr-cardh-x">0</span></div>
            <div className="crmr-dealempty"><div className="crmr-dealempty-ico">◷</div><p>Ticketing is coming soon. Customer requests for this company will appear here.</p></div>
          </div>
        </div>
      </div>

      {compose && (
        <CrmEmailComposer
          clientId={baseClientId}
          contact={compose.contact}
          replyTo={compose.replyTo}
          onClose={() => setCompose(null)}
          onSent={() => { setCompose(null); setEmailRefresh(k => k + 1); }}
        />
      )}

      {showAddContact && (
        <div className="crmr-modal-overlay" onClick={() => !creatingContact && !fetchingLi && setShowAddContact(false)}>
          <div className="crmr-modal crmr-modal-wide" onClick={e => e.stopPropagation()}>
            <h2>Add contact to {co.name}</h2>
            <p>Paste a LinkedIn profile to prefill, or enter the details by hand. The new contact is attached to this company automatically.</p>
            <div className="crmr-addfield">
              <label>LinkedIn profile URL</label>
              <div className="crmr-addli">
                <input value={addForm.linkedin_url} onChange={e => setAddForm(p => ({ ...p, linkedin_url: e.target.value }))} placeholder="https://www.linkedin.com/in/…" />
                <button className="crmr-act crmr-act-violet" onClick={fetchFromLinkedin} disabled={fetchingLi}>{fetchingLi ? 'Reading…' : 'Fetch'}</button>
              </div>
            </div>
            <div className="crmr-addfield"><label>Full name *</label><input value={addForm.full_name} onChange={e => setAddForm(p => ({ ...p, full_name: e.target.value }))} placeholder="Jane Doe" /></div>
            <div className="crmr-addfield"><label>Job title</label><input value={addForm.job_title} onChange={e => setAddForm(p => ({ ...p, job_title: e.target.value }))} placeholder="Head of Revenue" /></div>
            <div className="crmr-addfield"><label>Email</label><input value={addForm.email} onChange={e => setAddForm(p => ({ ...p, email: e.target.value }))} placeholder="jane@company.com" /></div>
            <div className="crmr-modal-actions">
              <button className="crmr-act crmr-act-ghost" onClick={() => setShowAddContact(false)} disabled={creatingContact}>Cancel</button>
              <button className="crmr-act crmr-act-primary" onClick={createContact} disabled={creatingContact}>{creatingContact ? 'Adding…' : 'Add contact'}</button>
            </div>
          </div>
        </div>
      )}

      {dealDrawer && (
        <DealDrawer
          opp={dealDrawer.opp}
          pipelines={pipelines}
          companies={company ? [company] : []}
          contacts={contacts}
          prefillCompanyId={company ? company.id : null}
          lockCompany
          clientId={baseClientId}
          onClose={() => setDealDrawer(null)}
          onSaved={() => { setDealDrawer(null); loadAll(); }}
        />
      )}
    </div>
  );
}
